import {
    makeStyles,
    Theme,
    createStyles,
    Typography,
} from "@material-ui/core";
import React, { Fragment, useEffect, useState } from "react";
import { Shift } from "../../../stores/models/Shift";
import { observer } from "mobx-react";
import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@material-ui/lab";
import { isValidEmail } from "../../../logic/ValidationChecks/Validation";
import { INotifiable } from "../../../stores/models/interfaces/INotifiable";
import NotificationRecipientSelect from "../../../shared/modules/input/select/NotificationRecipientSelect";
import { InheritanceModes } from "../../../stores/models/constants/InheritanceModes";
import LeadsAutocomplete from "../../../modules/recruitment/components/LeadsAutocomplete";
import REMChip from "../../../shared/modules/rem-conversion/components/Chip";
import { ClipboardText } from "mdi-material-ui";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        marginBottom: {
            marginBottom: theme.spacing(2)
        },
        highlight: {
            background: theme.palette.primaryBackground.main
        }
    })
);

/********* Helper methods *********/

// Get the unique elements in an array of objects based on a particular object property
const getUniqueBy = <T extends object, K extends keyof T>(arr: T[], key: K) => {
    const seen = new Set();
    return arr.filter(item => !seen.has(item[key]) && seen.add(item[key]));
};

/********* Component Props *********/

interface ShiftLeadsSelectionProps {
    opportunityLeads: INotifiable[];
    organizationAdmins: INotifiable[];
    shift: Shift;
}

/********* Component *********/

const ShiftLeadsSelection = observer(({
    opportunityLeads,
    organizationAdmins,
    shift
}: ShiftLeadsSelectionProps) => {

    /********* React Hooks *********/

    const classes = useStyles();

    /********* State *********/

    const [displayedLeads, setDisplayedLeads] = useState<INotifiable[]>([])

    /********* Effects *********/

    useEffect(() => {
        setDisplayedLeads(getInitialDisplayedLeadsBasedOnMode())
    }, [opportunityLeads]);

    /********* Helper methods *********/

    const getComboOfOpportunityAndShiftLeads = () => {
        const combo = [...opportunityLeads, ...shift.leads];
        const uniqueLeads = getUniqueBy(combo, 'email');
        return uniqueLeads;
    }

    const getInitialDisplayedLeadsBasedOnMode = () => {
        switch (shift.leadsMode) {
            case InheritanceModes.Inherit:
                return opportunityLeads;
            case InheritanceModes.Clear:
                return [];
            case InheritanceModes.Append:
                return getComboOfOpportunityAndShiftLeads();
            case InheritanceModes.Replace:
                return shift.leads;
        }
    }

    const updateShiftLeadsAccountingForLeadsMode = (newLeads: INotifiable[]) => {
        // Exclude opportunity leads from data saved to shift if already captured by the leads mode
        if (shift.leadsMode === InheritanceModes.Append || shift.leadsMode === InheritanceModes.Inherit) {
            const nonOpportunityLeadContacts = newLeads.filter(newLead => opportunityLeads.findIndex(oppLead => oppLead.email === newLead.email) === -1);
            shift.setLeads(nonOpportunityLeadContacts);
        } else {
            shift.setLeads(newLeads);
        }
    }

    /********* Event Handlers *********/

    const onLeadsModeChanged = (
        event: React.ChangeEvent<{ name?: string; value: unknown; }>,
        child: React.ReactNode
    ) => {

        const newLeadsMode = event.target.value as InheritanceModes;
        let newLeads = [] as INotifiable[];

        switch (newLeadsMode) {
            case InheritanceModes.Inherit:
                // Update leads to match the opportunity leads
                newLeads = opportunityLeads;
                break;
            case InheritanceModes.Clear:
                // Remove all leads
                newLeads = [];
                break;
            case InheritanceModes.Append:
                // Add opportunity leads if any were missing
                let missingOpportunityLeads = [] as INotifiable[];
                opportunityLeads.forEach(lead => {
                    if (shift.leads.findIndex(shiftLead => shiftLead.email === lead.email) === -1) {
                        missingOpportunityLeads.push(lead);
                    }
                })
                newLeads = [...shift.leads, ...missingOpportunityLeads];
                break;
            case InheritanceModes.Replace:
                // Do nothing, keep the value as is
                newLeads = displayedLeads;
                break;
        }

        shift.setLeadsMode(event.target.value as InheritanceModes)

        setDisplayedLeads(newLeads)
        updateShiftLeadsAccountingForLeadsMode(newLeads);
    }

    const onLeadsChanged = (
        event: React.ChangeEvent<{}>,
        value: (string | INotifiable)[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string | INotifiable>
    ) => {
        if (value.length > 0) {
            const lastValue = value[value.length - 1];
            if (typeof lastValue === 'string' && !isValidEmail(lastValue)) {
                return;
            }
        }

        const contacts = value.map(contact => {
            if (typeof contact === 'string') {
                return { email: contact };
            } else {
                return contact;
            }
        })
        setDisplayedLeads(contacts);


        let allOpportunityLeadsSelected = true;
        for (let i = 0; i < opportunityLeads.length; i++) {
            if (contacts.findIndex(contact => contact.email === opportunityLeads[i].email) === -1) {
                allOpportunityLeadsSelected = false;
                break;
            }
        }

        // Adjust the leads mode based on the updated leads selections:
        if (allOpportunityLeadsSelected) {
            if (shift.leadsMode !== InheritanceModes.Replace) {
                if (contacts.length > opportunityLeads.length) {
                    shift.setLeadsMode(InheritanceModes.Append);
                } else {
                    shift.setLeadsMode(InheritanceModes.Inherit);
                }
            }
        } else {
            if (contacts.length === 0) {
                shift.setLeadsMode(InheritanceModes.Clear);
            } else {
                shift.setLeadsMode(InheritanceModes.Replace);
            }
        }

        updateShiftLeadsAccountingForLeadsMode(contacts);
    }

    /********* Render *********/

    return (
        <Fragment>
            <div className={classes.marginBottom}>
                <NotificationRecipientSelect
                    value={shift.leadsMode}
                    onChange={onLeadsModeChanged}
                    options={{
                        inherit: "Same as Opportunity Leads",
                        append: "Opportunity Leads + Additional Recipients",
                        replace: "Only Specified Recipients",
                        clear: "No One"
                    }}
                />
            </div>
            {shift.leadsMode === InheritanceModes.Inherit || shift.leadsMode === InheritanceModes.Append
                ? <div className={classes.marginBottom}>
                    <REMChip
                        variant="outlined"
                        icon={<ClipboardText />}
                        label={"Opportunity Leads"}
                        className={classes.highlight}
                        style={{ marginRight: '4px', display: 'inline-flex', marginBottom: '4px' }}
                    />
                    <Typography variant="body2" style={{ display: 'inline' }}>
                        appear in blue below and are pulled in and updated dynamically from the opportunity.
                    </Typography>
                </div>
                : null
            }
            <LeadsAutocomplete
                options={organizationAdmins || []}
                highlightedOptions={shift.leadsMode === InheritanceModes.Inherit || shift.leadsMode === InheritanceModes.Append ? opportunityLeads : []}
                selections={displayedLeads}
                onChange={onLeadsChanged}
            />
        </Fragment>
    )
});

export default ShiftLeadsSelection;